import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2, Truck, CreditCard, Bell, Share2, ShieldCheck, FileText,
  Save, Loader2, Check, X, ArrowRight,
} from 'lucide-react';
import api from '@/lib/api';
import { BankAccountsPanel } from '@/components/seller/BankAutoConfirm';

/**
 * Multi-tab seller Settings page.
 * Tabs: 판매자 정보 · 배송 · 결제 · 알림 · SNS · 보안 · 이용약관
 * Real forms wire to /api/seller/{settings,shipping-policy,payment-config,sns};
 * 알림/보안/약관 are informational placeholders for later phases.
 */
const TABS = [
  { key: 'business', label: '판매자 정보', icon: Building2 },
  { key: 'shipping', label: '배송설정',   icon: Truck },
  { key: 'payment',  label: '결제설정',   icon: CreditCard },
  { key: 'notify',   label: '알림설정',   icon: Bell },
  { key: 'sns',      label: 'SNS 연동',   icon: Share2 },
  { key: 'security', label: '보안설정',   icon: ShieldCheck },
  { key: 'terms',    label: '이용약관',   icon: FileText },
];

const Row = ({ label, children, hint }) => (
  <div className="grid sm:grid-cols-3 gap-2 sm:gap-4 items-start py-2">
    <label className="text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 sm:pt-2">{label}</label>
    <div className="sm:col-span-2 space-y-1">
      {children}
      {hint && <p className="text-[11px] text-gray-400 leading-relaxed">{hint}</p>}
    </div>
  </div>
);

const SaveBar = ({ onSave, saving, dirty }) => (
  <div className="sticky bottom-0 -mx-4 sm:mx-0 mt-6 px-4 sm:px-0 py-3 border-t border-black/5 dark:border-white/10 bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur flex items-center justify-end gap-2">
    <button type="button" disabled={!dirty || saving} onClick={onSave}
      className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-40 text-white text-sm font-bold"
      data-testid="settings-save"
    >
      {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
      {saving ? '저장 중…' : '저장'}
    </button>
  </div>
);

const BusinessTab = () => {
  const [form, setForm] = useState({ business_name: '', ceo_name: '', business_number: '', mail_order_number: '', customer_center: '', contact_email: '', address: '' });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/seller/settings').then((r) => setForm({ ...form, ...r.data })).catch(() => {}); /* eslint-disable-line */ }, []);
  const patch = (p) => { setForm((f) => ({ ...f, ...p })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { await api.put('/seller/settings', form); setDirty(false); toast.success('판매자 정보가 저장되었습니다.'); }
    catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSaving(false); }
  };
  return (
    <>
      <Row label="상호"><input value={form.business_name} onChange={(e) => patch({ business_name: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="settings-business-name" /></Row>
      <Row label="대표자"><input value={form.ceo_name} onChange={(e) => patch({ ceo_name: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="settings-ceo" /></Row>
      <Row label="사업자번호"><input value={form.business_number} onChange={(e) => patch({ business_number: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" placeholder="000-00-00000" data-testid="settings-biz-no" /></Row>
      <Row label="통신판매업 신고번호"><input value={form.mail_order_number} onChange={(e) => patch({ mail_order_number: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="settings-mail-order" /></Row>
      <Row label="고객센터"><input value={form.customer_center} onChange={(e) => patch({ customer_center: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" placeholder="1588-0000" data-testid="settings-cs" /></Row>
      <Row label="이메일"><input type="email" value={form.contact_email} onChange={(e) => patch({ contact_email: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="settings-email" /></Row>
      <Row label="주소"><input value={form.address} onChange={(e) => patch({ address: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="settings-address" /></Row>
      <SaveBar onSave={save} saving={saving} dirty={dirty} />
    </>
  );
};

const ShippingTab = () => {
  const [form, setForm] = useState({ shipping_type: 'paid', base_fee: 3000, free_threshold: 50000, jeju_extra: 3000, remote_extra: 5000, use_bundle_shipping: true });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/seller/shipping-policy').then((r) => setForm({ ...form, ...r.data })).catch(() => {}); /* eslint-disable-line */ }, []);
  const patch = (p) => { setForm((f) => ({ ...f, ...p })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { await api.put('/seller/shipping-policy', form); setDirty(false); toast.success('배송설정이 저장되었습니다.'); }
    catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSaving(false); }
  };
  return (
    <>
      <Row label="배송 유형">
        <select value={form.shipping_type} onChange={(e) => patch({ shipping_type: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="shipping-type">
          <option value="free">무료배송</option>
          <option value="paid">유료배송</option>
          <option value="conditional">조건부 무료배송</option>
        </select>
      </Row>
      {form.shipping_type !== 'free' && (
        <Row label="기본 배송비"><input type="number" min="0" value={form.base_fee} onChange={(e) => patch({ base_fee: Number(e.target.value) })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="shipping-base-fee" /></Row>
      )}
      {form.shipping_type === 'conditional' && (
        <Row label="무료배송 기준 금액" hint="이 금액 이상 주문 시 배송비가 무료로 적용됩니다.">
          <input type="number" min="0" value={form.free_threshold} onChange={(e) => patch({ free_threshold: Number(e.target.value) })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="shipping-threshold" />
        </Row>
      )}
      <Row label="제주 추가배송비"><input type="number" min="0" value={form.jeju_extra} onChange={(e) => patch({ jeju_extra: Number(e.target.value) })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="shipping-jeju" /></Row>
      <Row label="도서산간 추가배송비"><input type="number" min="0" value={form.remote_extra} onChange={(e) => patch({ remote_extra: Number(e.target.value) })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="shipping-remote" /></Row>
      <Row label="묶음배송">
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.use_bundle_shipping} onChange={(e) => patch({ use_bundle_shipping: e.target.checked })} className="accent-[#FF3B30]" data-testid="shipping-bundle" /> 여러 상품을 하나로 묶어 배송합니다</label>
      </Row>
      <SaveBar onSave={save} saving={saving} dirty={dirty} />
    </>
  );
};

const PaymentTab = () => {
  const [data, setData] = useState(null);
  const [bank, setBank] = useState({ bank: '', account_holder: '', enabled: false, account_number: '' });
  const [pg, setPg] = useState({ provider: '', merchant_id: '', channel_id: '', enabled: false, api_key: '' });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const reload = () => api.get('/seller/payment-config').then((r) => {
    setData(r.data);
    setBank({ bank: r.data.bank_transfer.bank, account_holder: r.data.bank_transfer.account_holder, enabled: r.data.bank_transfer.enabled, account_number: '' });
    setPg({ provider: r.data.seller_pg.provider, merchant_id: r.data.seller_pg.merchant_id, channel_id: r.data.seller_pg.channel_id, enabled: r.data.seller_pg.enabled, api_key: '' });
  });
  useEffect(() => { reload(); }, []);
  const patchBank = (p) => { setBank((b) => ({ ...b, ...p })); setDirty(true); };
  const patchPg = (p) => { setPg((v) => ({ ...v, ...p })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      // Only send account_number / api_key when the user actually typed a new value;
      // this preserves existing encrypted secrets on partial updates.
      const bt = { ...bank };
      if (!bt.account_number) delete bt.account_number;
      const pv = { ...pg };
      if (!pv.api_key) delete pv.api_key;
      await api.put('/seller/payment-config', { bank_transfer: bt, seller_pg: pv });
      setDirty(false);
      toast.success('결제설정이 저장되었습니다.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSaving(false); }
  };
  if (!data) return <div className="text-sm text-gray-400 py-6">불러오는 중…</div>;

  return (
    <div className="space-y-6">
      <section data-testid="payment-bank">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-black tracking-tight">① 판매자 계좌입금</h3>
          <label className="inline-flex items-center gap-1 text-xs font-semibold">
            <input type="checkbox" checked={bank.enabled} onChange={(e) => patchBank({ enabled: e.target.checked })} className="accent-[#FF3B30]" data-testid="bank-enabled" />
            사용
          </label>
        </div>
        <Row label="은행"><input value={bank.bank} onChange={(e) => patchBank({ bank: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="bank-name" /></Row>
        <Row label="계좌번호" hint={data.bank_transfer.account_number_configured ? `현재 저장: ${data.bank_transfer.account_number_masked} (변경 시에만 입력)` : '한 번만 저장하면 서버에서 암호화 보관됩니다.'}>
          <input value={bank.account_number} onChange={(e) => patchBank({ account_number: e.target.value })} placeholder={data.bank_transfer.account_number_configured ? '(변경할 계좌번호 입력)' : ''} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="bank-account" />
        </Row>
        <Row label="예금주"><input value={bank.account_holder} onChange={(e) => patchBank({ account_holder: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="bank-holder" /></Row>
      </section>

      <BankAccountsPanel />

      <section data-testid="payment-pg" className="pt-4 border-t border-black/5 dark:border-white/10">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-black tracking-tight">② 판매자 PG</h3>
          <label className="inline-flex items-center gap-1 text-xs font-semibold">
            <input type="checkbox" checked={pg.enabled} onChange={(e) => patchPg({ enabled: e.target.checked })} className="accent-[#FF3B30]" data-testid="pg-enabled" />
            사용
          </label>
        </div>
        <Row label="PG사"><select value={pg.provider} onChange={(e) => patchPg({ provider: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="pg-provider">
          <option value="">선택</option>
          <option value="lightpay">라이트페이</option>
          <option value="toss">토스페이먼츠</option>
          <option value="portone">포트원</option>
          <option value="nicepay">나이스페이</option>
          <option value="inicis">이니시스</option>
          <option value="kakaopay">카카오페이</option>
        </select></Row>
        <Row label="가맹점 ID"><input value={pg.merchant_id} onChange={(e) => patchPg({ merchant_id: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="pg-merchant" /></Row>
        <Row label="채널 ID"><input value={pg.channel_id} onChange={(e) => patchPg({ channel_id: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="pg-channel" /></Row>
        <Row label="API Key" hint={data.seller_pg.api_key_configured ? `현재 저장: ${data.seller_pg.api_key_masked} (변경 시에만 입력)` : '평문으로 저장되지 않으며 서버에서 암호화됩니다.'}>
          <input value={pg.api_key} onChange={(e) => patchPg({ api_key: e.target.value })} placeholder={data.seller_pg.api_key_configured ? '(변경할 API Key 입력)' : ''} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="pg-api-key" />
        </Row>
        <Row label="연결 상태">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${data.seller_pg.connection_status === 'connected' ? 'bg-emerald-100 text-emerald-700' : data.seller_pg.connection_status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-testid="pg-status">
            {data.seller_pg.connection_status === 'connected' ? <><Check size={11} /> 연결됨</> : data.seller_pg.connection_status === 'pending' ? '검증 대기' : <><X size={11} /> 미연결</>}
          </span>
        </Row>
      </section>

      <SaveBar onSave={save} saving={saving} dirty={dirty} />
    </div>
  );
};

const SnsTab = () => {
  const [form, setForm] = useState({ tiktok: '', instagram: '', youtube: '', naver: '' });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // STEP 8-H-5 · YouTube OAuth connect/reconnect UI (기존 profile URL 과 분리)
  const [ytOAuth, setYtOAuth] = useState(null); // {oauth_connected, refresh_failed, channel_title, ...}
  const [ytBusy, setYtBusy] = useState(false);
  const loadYtStatus = React.useCallback(async () => {
    try { const r = await api.get('/seller/youtube/oauth/status'); setYtOAuth(r.data || null); }
    catch { setYtOAuth(null); }
  }, []);
  useEffect(() => { api.get('/seller/sns').then((r) => setForm({ ...form, ...r.data })).catch(() => {}); /* eslint-disable-line */ }, []);
  useEffect(() => {
    loadYtStatus();
    // Query param 감지: OAuth callback → ?yt_connected=1 또는 ?yt_error=...
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get('yt_connected') === '1') {
        toast.success('YouTube 계정이 연결되었습니다.');
        loadYtStatus();
      } else if (q.get('yt_error')) {
        toast.error(`YouTube 연결 실패 · ${q.get('yt_error')}`);
      }
    } catch {}
  }, [loadYtStatus]);
  const patch = (p) => { setForm((f) => ({ ...f, ...p })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { await api.put('/seller/sns', form); setDirty(false); toast.success('SNS 링크가 저장되었습니다.'); }
    catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSaving(false); }
  };
  const startYtOAuth = async () => {
    if (ytBusy) return;
    setYtBusy(true);
    try {
      const r = await api.get('/youtube/oauth/start');
      if (r?.data?.auth_url) {
        window.location.href = r.data.auth_url;
      } else {
        toast.error('OAuth 시작 실패 · auth_url 응답 없음');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'YouTube 연결 시작 실패');
    } finally { setYtBusy(false); }
  };
  const disconnectYt = async () => {
    if (ytBusy) return;
    if (!window.confirm('YouTube 계정 연결을 해제할까요? 댓글 수집이 중단될 수 있어요.')) return;
    setYtBusy(true);
    try {
      await api.post('/seller/youtube/oauth/disconnect', {});
      toast.success('YouTube 계정 연결이 해제되었습니다.');
      loadYtStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '연결 해제 실패');
    } finally { setYtBusy(false); }
  };
  const YtOAuthPanel = () => {
    if (!ytOAuth) {
      return <div className="text-[11px] text-gray-400" data-testid="sns-yt-oauth-loading">계정 연결 상태 불러오는 중…</div>;
    }
    if (!ytOAuth.oauth_available) {
      return <div className="text-[11px] text-gray-500" data-testid="sns-yt-oauth-unavailable">YouTube OAuth 설정이 아직 완료되지 않았습니다. (관리자 문의)</div>;
    }
    if (ytOAuth.refresh_failed) {
      return (
        <div className="rounded-xl border-2 border-red-500/40 bg-red-500/5 p-3 space-y-2" data-testid="sns-yt-oauth-reconnect">
          <div className="flex items-center gap-2">
            <span className="inline-flex w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[12px] font-black text-red-600">재연결 필요</span>
            <span className="text-[10px] text-gray-500">사유: {ytOAuth.refresh_failed_reason || 'unknown'}</span>
          </div>
          <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">
            YouTube 인증이 만료되었거나 취소되어 자동 갱신에 실패했어요. 다시 연결해 주세요.
          </p>
          <button type="button" onClick={startYtOAuth} disabled={ytBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-[12px] font-black"
            data-testid="sns-yt-oauth-reconnect-btn">
            {ytBusy ? '이동 중…' : 'YouTube 다시 연결'}
          </button>
        </div>
      );
    }
    if (ytOAuth.oauth_connected) {
      const mins = ytOAuth.expires_in_sec != null ? Math.max(0, Math.round(ytOAuth.expires_in_sec / 60)) : null;
      return (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-1.5" data-testid="sns-yt-oauth-connected">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[12px] font-black text-emerald-600">연결됨</span>
            {ytOAuth.channel_title && (
              <span className="text-[11px] text-gray-700 dark:text-gray-300 font-bold truncate">
                · {ytOAuth.channel_title}
              </span>
            )}
            {mins != null && (
              <span className="text-[10px] text-gray-500">· 토큰 갱신 대기 ~{mins}분</span>
            )}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={startYtOAuth} disabled={ytBusy}
              className="text-[11px] underline text-emerald-700 dark:text-emerald-400 hover:text-emerald-900"
              data-testid="sns-yt-oauth-refresh-btn">재연결</button>
            <button type="button" onClick={disconnectYt} disabled={ytBusy}
              className="text-[11px] underline text-gray-500 hover:text-red-500"
              data-testid="sns-yt-oauth-disconnect-btn">연결 해제</button>
          </div>
        </div>
      );
    }
    // Not connected + no failure marker
    return (
      <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 space-y-2" data-testid="sns-yt-oauth-idle">
        <div className="flex items-center gap-2">
          <span className="inline-flex w-2 h-2 rounded-full bg-gray-400" />
          <span className="text-[12px] font-bold text-gray-600 dark:text-gray-400">미연결</span>
        </div>
        <p className="text-[11px] text-gray-500 leading-relaxed">
          내 YouTube 계정을 연결하면 LIVE 방송 댓글이 Seller Console 통합댓글로 자동 수집돼요.
        </p>
        <button type="button" onClick={startYtOAuth} disabled={ytBusy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-50 text-white text-[12px] font-black"
          data-testid="sns-yt-oauth-connect-btn">
          {ytBusy ? '이동 중…' : 'YouTube 계정 연결'}
        </button>
      </div>
    );
  };
  return (
    <>
      <Row label="TikTok"><input value={form.tiktok} onChange={(e) => patch({ tiktok: e.target.value })} placeholder="https://tiktok.com/@yourshop" className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="sns-tiktok" /></Row>
      <Row label="Instagram"><input value={form.instagram} onChange={(e) => patch({ instagram: e.target.value })} placeholder="https://instagram.com/yourshop" className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="sns-instagram" /></Row>
      <Row label="YouTube 채널 URL" hint="프로필/스토어 페이지에 표시되는 채널 URL 입니다. 아래 [YouTube 계정 연결] 과는 별개입니다.">
        <input value={form.youtube} onChange={(e) => patch({ youtube: e.target.value })} placeholder="https://youtube.com/@yourshop" className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="sns-youtube" />
      </Row>
      <Row label="YouTube 계정 연결" hint="LIVE 댓글 수집을 위해 내 YouTube 계정을 OAuth 로 연결합니다. (읽기 권한만 요청)">
        <YtOAuthPanel />
      </Row>
      <Row label="네이버"><input value={form.naver} onChange={(e) => patch({ naver: e.target.value })} placeholder="네이버 쇼핑라이브 URL" className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="sns-naver" /></Row>
      <SaveBar onSave={save} saving={saving} dirty={dirty} />
    </>
  );
};

const ComingSoon = ({ title, description, testId }) => (
  <div className="py-10 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid={testId}>
    <p className="text-sm font-black tracking-widest uppercase text-[#FF3B30] mb-1">Coming Soon</p>
    <p className="text-base font-bold">{title}</p>
    <p className="text-xs text-gray-500 mt-1">{description}</p>
  </div>
);

const SettingsSection = () => {
  // STEP 8-H-5: SNS 탭 자동 선택 지원 · `?section=settings&tab=sns` 진입 시.
  // OAuth callback 이 이 URL 로 리다이렉트하므로 판매자가 자연스레 SNS 탭 안착.
  const initialTab = (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const t = q.get('tab');
      if (t && TABS.some((x) => x.key === t)) return t;
    } catch {}
    return 'business';
  })();
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="space-y-4" data-testid="settings-section">
      <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>설정</h2>
      <div className="flex overflow-x-auto gap-1 border-b border-black/5 dark:border-white/10 -mx-4 px-4 sm:mx-0 sm:px-0">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold whitespace-nowrap border-b-2 -mb-px ${tab === t.key ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-transparent text-gray-500 hover:text-black dark:hover:text-white'}`}
            data-testid={`settings-tab-${t.key}`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-[#141414] p-4 sm:p-6 divide-y divide-black/5 dark:divide-white/10">
        {tab === 'business' && <BusinessTab />}
        {tab === 'shipping' && <ShippingTab />}
        {tab === 'payment' && <PaymentTab />}
        {tab === 'sns' && <SnsTab />}
        {tab === 'notify' && (
          <div className="py-6 space-y-3" data-testid="settings-notify-panel">
            <Link to="/seller/notifications" data-testid="settings-notify-open"
              className="flex items-start justify-between gap-4 rounded-2xl border border-black/10 dark:border-white/10 p-4 bg-white dark:bg-[#0F0F0F] hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-black inline-flex items-center gap-1.5">
                  <Bell size={13} className="text-[#FF3B30]" /> 고객 알림톡 설정
                  <span className="text-[9px] font-black bg-orange-500 text-white px-1.5 py-0.5 rounded-full">TEST MODE</span>
                </p>
                <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                  카카오 알림톡은 <b>선택형 유료 기능</b>입니다. 이벤트별로 ON/OFF를 지정할 수 있고,
                  TEST MODE에서는 실제 발송 없이 시스템 로그에만 기록됩니다.
                </p>
              </div>
              <ArrowRight size={12} className="shrink-0 mt-1 text-[#FF3B30]" />
            </Link>
            <Link to="/seller/notifications/logs" data-testid="settings-notify-logs-open"
              className="flex items-start justify-between gap-4 rounded-2xl border border-black/10 dark:border-white/10 p-4 bg-white dark:bg-[#0F0F0F] hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
              <div className="min-w-0">
                <p className="text-sm font-black inline-flex items-center gap-1.5">
                  <Bell size={13} className="text-[#FF3B30]" /> 알림 발송내역
                </p>
                <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                  실제로 발송된 알림톡, 스킵된 이벤트, 실패한 발송을 한 곳에서 확인하고 세부 정보를 열람할 수 있습니다.
                </p>
              </div>
              <ArrowRight size={12} className="shrink-0 mt-1 text-[#FF3B30]" />
            </Link>
          </div>
        )}
        {tab === 'security' && <ComingSoon title="보안설정" description="비밀번호 변경 · 2단계 인증 · 세션 관리를 다음 업데이트에서 지원합니다." testId="settings-security-placeholder" />}
        {tab === 'terms' && <ComingSoon title="이용약관" description="판매자 이용약관 · 개인정보처리방침 · 결제 및 배송 정책 문서가 곧 게시됩니다." testId="settings-terms-placeholder" />}
      </div>
    </div>
  );
};

export default SettingsSection;
